import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  actions,
  CellProps,
  Hooks,
  Row,
  SortingRule,
  TableState,
  useFlexLayout,
  usePagination,
  useResizeColumns,
  useSortBy,
  useTable,
} from 'react-table';
import type { AxiosResponse } from 'axios';
import DataTablePagination from 'components/v2/DataTable/DataTablePagination';
import DataTableBody from 'components/v2/DataTable/DataTableBody';
import DataTableHeader from 'components/v2/DataTable/DataTableHeader';
import URL_PARAMS, {
  getDataTableURLParamKeys,
} from 'components/v2/DataTable/data/urlParams';
import { DataTableTopBar, Loading, UIMessage } from 'components';
import NoResultsImage from 'img/blank-states/search.svg';
import classnames from 'classnames';
import usePrevious from 'infrastructure/hooks/usePrevious';
import getInitialSorting from 'components/v2/DataTable/helpers/getInitialSorting';
import getSortingParamsFromURL from 'components/v2/DataTable/helpers/getSortingParamsFromURL';
import { SortingDirection } from 'components/v2/DataTable/helpers/getSortingDirection';
import useURLParams from 'components/v2/DataTable/helpers/useURLParams';
import {
  getLocalFilter,
  saveLocalFilter,
} from 'components/v2/DataTable/helpers/localFilters';
import { DisplayOrderChangeType } from 'slices/StudioAssets/Templates/types';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useDebouncedCallback } from 'use-debounce';
import { useLocation } from 'react-router';
import type { LinkProps } from 'react-router-dom';
import type { TopBarConfig, TopBarProps } from './TopBar';
import {
  ButtonDown,
  ButtonUp,
  CustomButton,
  DuplicateButton,
  DownloadButton,
  EditButton,
  GetLatestButton,
  RemoveButton,
} from './Buttons/actionButtons';

import './DataTable.scss';

export interface DataTableColumn {
  header: string | JSX.Element;
  accessor: string;
  disableSortBy?: boolean;
}

export interface PaginatedListResponse<Data> {
  items: Array<Data>;
  totalCount: number;
}

export interface DataTablePaginationInfo {
  pageSize: number;
  pageIndex: number;
  totalElementsCount: number;
}

export type SortedColumn = { id?: string; desc?: boolean };

export type DataTableFetchProps = {
  pageSize: number;
  pageIndex: number;
  sortedColumn?: SortedColumn;
};

export type DataTableFetchFunction<Data extends object = {}> = (args: {
  pageSize: number;
  pageIndex: number;
  sortedColumn?: SortedColumn;
}) => Promise<AxiosResponse<PaginatedListResponse<Data>>>;

export interface DataTableReorderHelpers<Data extends object> {
  previousData: Array<Data>;
  nextData: Array<Data>;
  requiresReload: boolean;
  reload: () => void;
  rollback: () => void;
}

type DataTableActionOrder = 'default' | 'reorder-remove-edit';

export interface DataTableProps<Data extends object> {
  // Additional class name for the table
  className?: string;
  // ID for e.g. testing purposes
  tableId: string;
  // Structure interface of columns in the table
  columns: Array<DataTableColumn>;
  // Array of objects with data
  defaultData?: Array<Data>;
  /** Paginate already-loaded defaultData without an asynchronous fetch. */
  clientSidePagination?: boolean;
  // Promise returning the data that should be loaded
  fetchData?: DataTableFetchFunction<Data>;
  // Component that will be upon the table, e. g. inputs, selects, search bar etc. Optional, defaults to `components/v2/DataTable/TopBar`
  topBar?: React.FC<TopBarProps>;
  // Config for `topBar` component
  topBarConfig?: TopBarConfig;
  /** Keep a page-level toolbar in sync with this table's visible range. */
  onPaginationInfoChange?: (info: DataTablePaginationInfo) => void;
  getRowEditPath?: (row: Row<Data>) => LinkProps['to'];
  disableRowEdit?: (row: Row<Data>) => boolean;
  onRowRemove?: (row: Row<Data>) => void;
  disableRowRemove?: (row: Row<Data>) => boolean;
  hideRowRemove?: (row: Row<Data>) => boolean;
  onReorder?: (
    reorderData: DisplayOrderChangeType,
    helpers?: DataTableReorderHelpers<Data>
  ) => void;
  actionOrder?: DataTableActionOrder;
  optimisticReordering?: boolean;
  onRowDownload?: (row: Row<Data>) => void;
  onRowDuplicate?: (row: Row<Data>) => void;
  customButton?: {
    text?: string;
    icon?: IconDefinition;
    iconAfter?: IconDefinition;
    className?: string;
    tooltip?: string;
    onCustomButton: (row: Row<Data>) => void;
  };
  onRowGetLatest?: (row: Row<Data>) => void;
  customBodyItem?: (row: Row<Data> | Record<'row', Row<Data>>) => JSX.Element;
  customLastBodyItem?: () => JSX.Element;
  isDeleteRequestPending?: boolean;
  defaultSortBy?: SortingRule<{}>[];
  /**
   * Opt-in controlled sorting. When supplied, these rules are the table sort:
   * DataTable neither reads nor writes sort URL params, a column click is only
   * reported through `onSortByChange` (toggling that column between ascending
   * and descending), and a later change returns the table to its first page.
   * Omit it to keep the built-in sorting and URL behaviour.
   */
  sortBy?: SortingRule<{}>[];
  onSortByChange?: (sortBy: SortingRule<{}>[]) => void;
  noActionsColumn?: boolean;
  noTableHeader?: boolean;
  /**
   * Boolean value: table uses its default 6 column grid
   * String value: table uses custom column grid (Currently 'grid-4' available)
   */
  gridLayout?: boolean | string;
  defaultPageSize?: number;
  displayItemsNumberOptions?: number[];
  itemName?: string;
  enableReordering?: boolean;
  defaultPageCount?: number;
  disableURLParams?: boolean;
  /**
   * Optional URL namespace for independent table state, for example
   * `table.stories.story.pageViews`.
   */
  urlParamNamespace?: string;
  hideDisplayedElementsDropdown?: boolean;
  customBlankPage?: ReactElement;
  /** Opt-in presentation for a result group with no rows. */
  emptyStateMode?: 'default' | 'message-only' | 'hidden';
  hidePagination?: boolean;
  areTableDependeciesLoading?: boolean;
  dontGrowActionsColumn?: boolean;
  cancelFetchData?: () => void;
  /** Changing this value reloads the current page without resetting table state. */
  reloadKey?: unknown;
  /** Reload value last observed before this table instance mounted. */
  initialReloadKey?: unknown;
  /** Keep the current rows visible after the table has completed its first load. */
  preserveContentWhileRefetching?: boolean;
  /** Confirmed rows to prepend to the current first page before reconciliation. */
  upsertData?: readonly Data[];
  /** Stable identifier used to deduplicate confirmed rows. */
  getDataId?: (data: Data) => string | undefined;
}

const ACTIONS_COLUMN_ACCESSOR = 'action';

const getSortByKey = (sortBy: SortingRule<{}>[] = []) =>
  JSON.stringify(sortBy.map(({ id, desc }) => [id, Boolean(desc)]));

type ControlledSortToggle = (columnId: string, sortDescFirst?: boolean) => void;

type SortByTogglePropGetter = (
  props: object,
  meta: {
    instance: object;
    column: { id: string; canSort: boolean; sortDescFirst?: boolean };
  }
) => object | object[];

// With controlled sorting, a header click reports the next rules to the owner
// instead of changing react-table's own sort state.
const useControlledSortToggle = (hooks: Hooks<any>) => {
  (hooks.getSortByToggleProps as unknown as SortByTogglePropGetter[]).push(
    (props, { instance, column }) => {
      const { onControlledSortToggle } = instance as unknown as {
        onControlledSortToggle?: ControlledSortToggle;
      };
      if (!onControlledSortToggle || !column.canSort) {
        return props;
      }

      return [
        props,
        {
          onClick: () =>
            onControlledSortToggle(column.id, column.sortDescFirst),
        },
      ];
    }
  );
};
useControlledSortToggle.pluginName = 'useControlledSortToggle';

const generateColumns = <Data extends object>(
  originColumns: DataTableProps<Data>['columns'],
  onRowRemove: DataTableProps<Data>['onRowRemove'],
  disableRowRemove: DataTableProps<Data>['disableRowRemove'],
  hideRowRemove: DataTableProps<Data>['hideRowRemove'],
  getRowEditPath: DataTableProps<Data>['getRowEditPath'],
  disableRowEdit: DataTableProps<Data>['disableRowEdit'],
  onReorder: DataTableProps<Data>['onReorder'],
  onRowDownload: DataTableProps<Data>['onRowDownload'],
  onRowDuplicate: DataTableProps<Data>['onRowDuplicate'],
  customButton: DataTableProps<Data>['customButton'],
  onRowGetLatest: DataTableProps<Data>['onRowGetLatest'],
  tableId: DataTableProps<Data>['tableId'],
  firstElementId?: string,
  lastElementId?: string,
  disableReorder?: boolean,
  dontGrowActionsColumn?: boolean,
  actionOrder: DataTableProps<Data>['actionOrder'] = 'default'
) => {
  const doesActionsExists = originColumns.find(
    column => column.accessor === ACTIONS_COLUMN_ACCESSOR
  );
  if (doesActionsExists) {
    return originColumns;
  }

  const customButtonWidth = customButton?.text ? 100 : 40;

  return [
    ...originColumns,
    {
      header: 'Actions',
      accessor: ACTIONS_COLUMN_ACCESSOR,
      disableSortBy: true,
      className: 'actions',
      minWidth:
        (onReorder ? 225 : 160) + (customButton ? customButtonWidth : 0),
      dontGrow: dontGrowActionsColumn,
      Cell: ({ row }: CellProps<Data>) => {
        const customActions = (
          <>
            {customButton && (
              <CustomButton row={row} tableId={tableId} {...customButton} />
            )}
            {onRowDownload && (
              <DownloadButton
                onRowDownload={onRowDownload}
                row={row}
                tableId={tableId}
              />
            )}
            {onRowDuplicate && (
              <DuplicateButton
                onRowDuplicate={onRowDuplicate}
                row={row}
                tableId={tableId}
              />
            )}
          </>
        );

        const removeAction = onRowRemove ? (
          <RemoveButton
            onRowRemove={onRowRemove}
            row={row}
            tableId={tableId}
            disableRowRemove={disableRowRemove}
            hideRowRemove={hideRowRemove}
          />
        ) : null;

        const editAction = getRowEditPath ? (
          <EditButton
            getRowEditPath={getRowEditPath}
            row={row}
            tableId={tableId}
            disableRowEdit={disableRowEdit}
          />
        ) : null;

        const reorderActions =
          onReorder && !disableReorder ? (
            <>
              <ButtonUp
                onReorder={onReorder}
                firstElementId={firstElementId}
                row={row}
                tableId={tableId}
              />
              <ButtonDown
                onReorder={onReorder}
                lastElementId={lastElementId}
                row={row}
                tableId={tableId}
              />
            </>
          ) : null;

        const getLatestAction = onRowGetLatest ? (
          <GetLatestButton
            onRowGetLatest={onRowGetLatest}
            row={row}
            tableId={tableId}
          />
        ) : null;

        return (
          <div className="action-cell">
            {customActions}
            {actionOrder === 'reorder-remove-edit' ? (
              <>
                {reorderActions}
                {removeAction}
                {editAction}
              </>
            ) : (
              <>
                {removeAction}
                {editAction}
                {reorderActions}
              </>
            )}
            {getLatestAction}
          </div>
        );
      },
    },
  ];
};

const DataTable = <Data extends { id?: string }>({
  className,
  tableId,
  columns,
  fetchData,
  topBar,
  topBarConfig,
  onPaginationInfoChange,
  getRowEditPath,
  disableRowEdit,
  onRowRemove,
  disableRowRemove,
  hideRowRemove,
  onReorder,
  actionOrder = 'default',
  optimisticReordering = false,
  onRowDownload,
  onRowDuplicate,
  customButton,
  onRowGetLatest,
  defaultData = [],
  clientSidePagination = false,
  gridLayout = false,
  isDeleteRequestPending = false,
  defaultSortBy,
  sortBy: controlledSortBy,
  onSortByChange,
  noActionsColumn = false,
  noTableHeader = false,
  customBodyItem,
  customLastBodyItem,
  defaultPageSize = 10,
  displayItemsNumberOptions = [10, 20, 50, 100],
  itemName = 'Rows',
  enableReordering,
  defaultPageCount,
  disableURLParams,
  urlParamNamespace,
  hideDisplayedElementsDropdown,
  customBlankPage,
  emptyStateMode = 'default',
  hidePagination = false,
  areTableDependeciesLoading = false,
  dontGrowActionsColumn,
  cancelFetchData,
  reloadKey,
  initialReloadKey,
  preserveContentWhileRefetching = false,
  upsertData,
  getDataId,
}: DataTableProps<Data>) => {
  const {
    handleURLSearchQueryParam,
    setURLParams,
    setURLSortingParams,
    unsetURLSortingParams,
  } = useURLParams({
    useRouterHistory: Boolean(urlParamNamespace),
  });
  const { search } = useLocation();
  const urlSearch = urlParamNamespace ? search : window.location.search;
  const urlParamKeys = useMemo(
    () => getDataTableURLParamKeys(urlParamNamespace),
    [urlParamNamespace]
  );
  const shouldUseURLParams = Boolean(urlParamNamespace) || !disableURLParams;
  const isSortingControlled = controlledSortBy !== undefined;
  const controlledSortByKey = isSortingControlled
    ? getSortByKey(controlledSortBy)
    : undefined;
  const shouldUseURLSorting = shouldUseURLParams && !isSortingControlled;
  const urlSearchParams = new URLSearchParams(urlSearch);
  const hasNamespacedTableURLState = Boolean(
    urlParamNamespace &&
      Object.values(urlParamKeys).some(paramName =>
        urlSearchParams.has(paramName)
      )
  );
  const shouldFetchInitialNamespacedURLState = Boolean(
    fetchData && defaultData.length > 0 && hasNamespacedTableURLState
  );
  const [data, setData] = useState(defaultData);
  const tableData = clientSidePagination ? defaultData : data;
  const [pageCount, setPageCount] = useState(
    defaultData ? defaultPageCount || 1 : 0
  );
  const [totalElementsCount, setTotalElementsCount] = useState(0);
  const [isRequestPending, setIsRequestPending] = useState(
    Boolean(fetchData) &&
      (defaultData.length === 0 || shouldFetchInitialNamespacedURLState) &&
      !areTableDependeciesLoading
  );
  const [disableInitialLoad, setDisableInitialLoad] = useState(
    defaultData.length > 0 && !shouldFetchInitialNamespacedURLState
  );
  const [reloadToken, setReloadToken] = useState(0);
  const externalReloadKeyRef = useRef(
    initialReloadKey === undefined ? reloadKey : initialReloadKey
  );
  const hasCompletedInitialFetchRef = useRef(
    !fetchData || defaultData.length > 0
  );

  const [firstElementId, setFirstElementId] = useState<string>();
  const [lastElementId, setLastElementId] = useState<string>();
  const latestFetchRequestId = useRef(0);
  const pageIndexRef = useRef(0);
  const hasInitializedPaginationURLRef = useRef(false);
  const hasInitializedSortingURLRef = useRef(false);
  const paginationObservedSearchRef = useRef(urlSearch);
  const paginationHydrationSearchRef = useRef(search);
  const sortingObservedSearchRef = useRef(urlSearch);
  const isHydratingPaginationFromURLRef = useRef(false);
  const isHydratingSortingFromURLRef = useRef(false);
  const appliedUpsertIdsRef = useRef<Set<string>>(new Set());

  const enableReorder =
    enableReordering && !topBarConfig?.searchFeature?.searchQuery;

  const reloadData = useCallback(() => {
    setReloadToken(token => token + 1);
  }, []);

  useEffect(() => {
    if (externalReloadKeyRef.current === reloadKey) {
      return;
    }

    externalReloadKeyRef.current = reloadKey;
    reloadData();
  }, [reloadData, reloadKey]);

  const getOptimisticReorderedData = useCallback(
    (reorderData: DisplayOrderChangeType) => {
      const currentIndex = data.findIndex(item => item.id === reorderData.id);
      const nextIndex =
        reorderData.direction === 'up' ? currentIndex - 1 : currentIndex + 1;

      if (currentIndex < 0 || nextIndex < 0 || nextIndex >= data.length) {
        return undefined;
      }

      const nextData = [...data];
      [nextData[currentIndex], nextData[nextIndex]] = [
        nextData[nextIndex],
        nextData[currentIndex],
      ];

      return {
        previousData: data,
        nextData,
      };
    },
    [data]
  );

  const isPageBoundaryReorder = useCallback(
    (reorderData: DisplayOrderChangeType) => {
      const currentIndex = data.findIndex(item => item.id === reorderData.id);

      return (
        (reorderData.direction === 'up' &&
          currentIndex === 0 &&
          pageIndexRef.current > 0) ||
        (reorderData.direction === 'down' &&
          currentIndex === data.length - 1 &&
          pageIndexRef.current < pageCount - 1)
      );
    },
    [data, pageCount]
  );

  const handleReorder = useCallback(
    (reorderData: DisplayOrderChangeType) => {
      if (!onReorder) {
        return;
      }

      if (!optimisticReordering) {
        onReorder(reorderData);
        return;
      }

      const optimisticData = getOptimisticReorderedData(reorderData);
      if (!optimisticData) {
        onReorder(reorderData, {
          previousData: data,
          nextData: data,
          requiresReload: isPageBoundaryReorder(reorderData),
          reload: reloadData,
          rollback: () => setData(data),
        });
        return;
      }

      setData(optimisticData.nextData);
      onReorder(reorderData, {
        ...optimisticData,
        requiresReload: false,
        reload: reloadData,
        rollback: () => setData(optimisticData.previousData),
      });
    },
    [
      data,
      getOptimisticReorderedData,
      isPageBoundaryReorder,
      onReorder,
      optimisticReordering,
      reloadData,
    ]
  );

  const columnsWithButtons = useMemo(
    () =>
      generateColumns<Data>(
        columns,
        onRowRemove,
        disableRowRemove,
        hideRowRemove,
        getRowEditPath,
        disableRowEdit,
        onReorder ? handleReorder : undefined,
        onRowDownload,
        onRowDuplicate,
        customButton,
        onRowGetLatest,
        tableId,
        firstElementId,
        lastElementId,
        !enableReorder,
        dontGrowActionsColumn,
        actionOrder
      ),
    [
      columns,
      onRowRemove,
      getRowEditPath,
      onReorder,
      handleReorder,
      onRowDownload,
      onRowDuplicate,
      customButton,
      onRowGetLatest,
      tableId,
      firstElementId,
      lastElementId,
      enableReorder,
      disableRowEdit,
      disableRowRemove,
      hideRowRemove,
      dontGrowActionsColumn,
      actionOrder,
    ]
  );

  const getInitialPageSize = () => {
    const localPageSize = Number(
      getLocalFilter(tableId, URL_PARAMS.displayElementsNumber)
    );
    const urlPageSize = shouldUseURLParams
      ? Number(
          new URLSearchParams(urlSearch).get(urlParamKeys.displayElementsNumber)
        )
      : 0;
    const validURLPageSize = displayItemsNumberOptions.includes(urlPageSize)
      ? urlPageSize
      : 0;
    const shouldUseLocalPageSize = !validURLPageSize && localPageSize;
    const shouldUseURLPageSize = Boolean(validURLPageSize);
    if (shouldUseLocalPageSize) {
      return localPageSize;
    }
    if (shouldUseURLPageSize) {
      return validURLPageSize;
    }
    return defaultPageSize;
  };

  const sortableColumnIds = useMemo(
    () =>
      columns
        .filter(column => !column.disableSortBy)
        .map(column => column.accessor),
    [columns]
  );
  const urlSortingSearchParams = new URLSearchParams(urlSearch);
  const requestedURLSortId = urlSortingSearchParams.get(urlParamKeys.sortId);
  const requestedURLSortDirection = urlSortingSearchParams.get(
    urlParamKeys.sorting
  );
  const hasRequestedURLSorting = Boolean(
    requestedURLSortId || requestedURLSortDirection
  );
  const hasValidURLSortDirection =
    requestedURLSortDirection === SortingDirection.asc ||
    requestedURLSortDirection === SortingDirection.desc;
  const hasInvalidURLSorting = Boolean(
    shouldUseURLSorting &&
      hasRequestedURLSorting &&
      (!requestedURLSortId ||
        !sortableColumnIds.includes(requestedURLSortId) ||
        !hasValidURLSortDirection)
  );
  const initialSortingParams = isSortingControlled
    ? controlledSortBy
    : getInitialSorting(
        defaultSortBy,
        sortableColumnIds,
        shouldUseURLParams ? urlParamKeys : undefined,
        shouldUseURLParams ? urlSearch : ''
      );
  const requestedInitialPageIndex = Number(
    new URLSearchParams(urlSearch).get(urlParamKeys.pageIndex)
  );
  const initialPageIndex =
    shouldUseURLParams &&
    Number.isInteger(requestedInitialPageIndex) &&
    requestedInitialPageIndex >= 0
      ? requestedInitialPageIndex
      : 0;
  const tableInstance = useTable(
    {
      // please excuse `any` below, TS support in React Table 7 is not so great...
      columns: !noActionsColumn ? (columnsWithButtons as any) : columns,
      data: tableData,
      initialState: {
        pageIndex: initialPageIndex,
        pageSize: getInitialPageSize(),
        ...(initialSortingParams?.length && { sortBy: initialSortingParams }),
      },
      manualPagination: !clientSidePagination,
      ...(clientSidePagination ? {} : { pageCount }),
      autoResetRowState: false,
      autoResetPage: false,
      autoResetSortBy: false,
      manualSorting: true,
      manualSortBy: true,
      useControlledState: (state: TableState<any>) =>
        // react-table calls this as a hook on every render.
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useMemo(
          () =>
            isSortingControlled
              ? { ...state, sortBy: controlledSortBy ?? [] }
              : state,
          // eslint-disable-next-line react-hooks/exhaustive-deps
          [state, controlledSortByKey]
        ),
      onControlledSortToggle: isSortingControlled
        ? (columnId: string, sortDescFirst?: boolean) => {
            const [current] = controlledSortBy ?? [];
            const isCurrentColumn =
              controlledSortBy?.length === 1 && current?.id === columnId;
            onSortByChange?.([
              {
                id: columnId,
                desc: isCurrentColumn ? !current.desc : Boolean(sortDescFirst),
              },
            ]);
          }
        : undefined,
    },
    useFlexLayout,
    useSortBy,
    useControlledSortToggle,
    usePagination,
    useResizeColumns
  );

  const {
    getTableProps,
    dispatch,
    state: { pageIndex, pageSize, sortBy },
    gotoPage,
    setPageSize,
  } = tableInstance;

  pageIndexRef.current = pageIndex;

  useEffect(() => {
    if (clientSidePagination && !areTableDependeciesLoading) {
      const lastPage = Math.max(Math.ceil(tableData.length / pageSize) - 1, 0);
      if (pageIndex > lastPage) gotoPage(lastPage);
    }
  }, [
    clientSidePagination,
    areTableDependeciesLoading,
    tableData.length,
    pageIndex,
    pageSize,
    gotoPage,
  ]);

  // A controlled sort change after mount starts again from the first page.
  const appliedControlledSortByKeyRef = useRef(controlledSortByKey);
  useEffect(() => {
    if (appliedControlledSortByKeyRef.current === controlledSortByKey) {
      return;
    }

    appliedControlledSortByKeyRef.current = controlledSortByKey;
    gotoPage(0);
  }, [controlledSortByKey, gotoPage]);

  useEffect(() => {
    if (pageIndex !== 0 || !getDataId || !upsertData?.length) {
      return;
    }

    const currentIds = new Set(
      data.map(getDataId).filter((id): id is string => typeof id === 'string')
    );
    const newRows: Data[] = [];

    upsertData.forEach(row => {
      const id = getDataId(row);
      if (!id || appliedUpsertIdsRef.current.has(id)) {
        return;
      }

      appliedUpsertIdsRef.current.add(id);
      if (!currentIds.has(id)) {
        currentIds.add(id);
        newRows.push(row);
      }
    });

    if (newRows.length) {
      const newRowIds = new Set(newRows.map(row => getDataId(row)));
      setData(currentData =>
        [
          ...newRows,
          ...currentData.filter(row => !newRowIds.has(getDataId(row))),
        ].slice(0, pageSize)
      );
    }

    const nextTotalElementsCount = Math.max(
      totalElementsCount + newRows.length,
      currentIds.size
    );
    if (nextTotalElementsCount !== totalElementsCount) {
      setTotalElementsCount(nextTotalElementsCount);
      setPageCount(Math.max(Math.ceil(nextTotalElementsCount / pageSize), 1));
    }
  }, [data, getDataId, pageIndex, pageSize, totalElementsCount, upsertData]);

  useEffect(() => {
    if (shouldUseURLSorting && hasInvalidURLSorting) {
      unsetURLSortingParams(urlParamKeys);
    }
  }, [
    hasInvalidURLSorting,
    shouldUseURLSorting,
    unsetURLSortingParams,
    urlParamKeys,
  ]);

  useEffect(() => {
    if (!onReorder) return;
    if (pageIndex === 0) setFirstElementId(data[0]?.id);
    if (pageIndex === pageCount - 1)
      setLastElementId(data[data.length - 1]?.id);
  }, [pageIndex, pageCount, data, onReorder]);

  useEffect(() => {
    saveLocalFilter(tableId, URL_PARAMS.displayElementsNumber, pageSize);
    if (!shouldUseURLParams) return;
    const searchChanged = paginationObservedSearchRef.current !== urlSearch;
    const currentSearchParams = new URLSearchParams(urlSearch);
    const pageIndexValue = currentSearchParams.get(urlParamKeys.pageIndex);
    const currentPageIndexInUrl = Number(pageIndexValue);
    const currentPageSizeInUrl = Number(
      currentSearchParams.get(urlParamKeys.displayElementsNumber)
    );
    const resolvedPageIndexInUrl =
      pageIndexValue !== null &&
      Number.isInteger(currentPageIndexInUrl) &&
      currentPageIndexInUrl >= 0
        ? currentPageIndexInUrl
        : 0;
    const resolvedPageSizeInUrl = displayItemsNumberOptions.includes(
      currentPageSizeInUrl
    )
      ? currentPageSizeInUrl
      : pageSize;

    if (
      searchChanged &&
      (pageIndex !== resolvedPageIndexInUrl ||
        pageSize !== resolvedPageSizeInUrl)
    ) {
      isHydratingPaginationFromURLRef.current = true;
      return;
    }

    // Keep the incoming URL pending until the table has adopted its page.
    // Query-cache renders can occur while that state update is still queued.
    paginationObservedSearchRef.current = urlSearch;

    const keepHistory =
      hasInitializedPaginationURLRef.current &&
      !searchChanged &&
      !isHydratingPaginationFromURLRef.current &&
      (pageIndex !== currentPageIndexInUrl ||
        pageSize !== currentPageSizeInUrl);
    setURLParams(
      {
        [urlParamKeys.displayElementsNumber]: `${pageSize}`,
        [urlParamKeys.pageIndex]: `${pageIndex}`,
      },
      keepHistory
    );
    hasInitializedPaginationURLRef.current = true;
    isHydratingPaginationFromURLRef.current = false;
  }, [
    displayItemsNumberOptions,
    pageIndex,
    pageSize,
    setURLParams,
    shouldUseURLParams,
    tableId,
    urlParamKeys,
    urlSearch,
  ]);

  const prevSortBy = usePrevious(sortBy)?.[0];

  useEffect(() => {
    if (enableReorder || (urlParamNamespace && !topBarConfig?.searchFeature)) {
      return;
    }
    handleURLSearchQueryParam(topBarConfig?.searchFeature?.searchQuery);
  }, [
    enableReorder,
    handleURLSearchQueryParam,
    topBarConfig,
    urlParamNamespace,
  ]);

  // Sorting from  URL handling
  useEffect(() => {
    if (!shouldUseURLSorting) return;
    if (urlParamNamespace && sortingObservedSearchRef.current !== urlSearch) {
      sortingObservedSearchRef.current = urlSearch;
      isHydratingSortingFromURLRef.current = true;
      return;
    }

    const sortingParams = sortBy?.[0] ?? {};
    const { id, desc } = sortingParams;
    const isSortingUsed = Boolean(id) && typeof desc !== 'undefined';
    const sortingChanged =
      JSON.stringify(prevSortBy ?? {}) !== JSON.stringify(sortingParams);
    const keepHistory =
      Boolean(urlParamNamespace) &&
      hasInitializedSortingURLRef.current &&
      !isHydratingSortingFromURLRef.current;

    if (isSortingUsed && sortingChanged) {
      setURLSortingParams(sortingParams, urlParamKeys, keepHistory);
    }
    if (!isSortingUsed && prevSortBy?.id) {
      unsetURLSortingParams(urlParamKeys, keepHistory);
    }
    hasInitializedSortingURLRef.current = true;
    isHydratingSortingFromURLRef.current = false;
  }, [
    prevSortBy,
    setURLSortingParams,
    shouldUseURLSorting,
    sortBy,
    unsetURLSortingParams,
    urlParamKeys,
    urlParamNamespace,
    urlSearch,
  ]);

  const setTableSortingSettingsAccordingToURLParams = useCallback((): void => {
    if (!shouldUseURLSorting) {
      return;
    }

    const sortingSearch = urlParamNamespace ? search : window.location.search;
    const params = getSortingParamsFromURL(urlParamKeys, sortingSearch);
    if (params.id && !sortableColumnIds.includes(params.id)) {
      tableInstance.setSortBy([]);
      unsetURLSortingParams(urlParamKeys);
      return;
    }

    const nextSortBy = params.id ? [params] : [];
    if (JSON.stringify(sortBy) === JSON.stringify(nextSortBy)) {
      isHydratingSortingFromURLRef.current = false;
      return;
    }
    tableInstance.setSortBy(nextSortBy);
  }, [
    search,
    shouldUseURLSorting,
    sortBy,
    sortableColumnIds,
    tableInstance,
    unsetURLSortingParams,
    urlParamKeys,
    urlParamNamespace,
  ]);

  useEffect(() => {
    if (isSortingControlled) {
      return undefined;
    }

    if (urlParamNamespace) {
      setTableSortingSettingsAccordingToURLParams();
      return undefined;
    }

    window.addEventListener(
      'popstate', // this event occurs whenever user is clicking back/forward button in browser
      setTableSortingSettingsAccordingToURLParams,
      false
    );
    return () =>
      window.removeEventListener(
        'popstate',
        setTableSortingSettingsAccordingToURLParams,
        false
      );
  }, [
    isSortingControlled,
    search,
    setTableSortingSettingsAccordingToURLParams,
    urlParamNamespace,
  ]);

  const sortedColumn = sortBy?.[0] || {};
  const fetchAllData = useDebouncedCallback(
    (fetchRequestId: number) =>
      fetchData?.({ pageIndex, pageSize, sortedColumn })
        .then(response => {
          if (fetchRequestId !== latestFetchRequestId.current) {
            return;
          }

          const nextPageCount = Math.ceil(response.data.totalCount / pageSize);
          const maximumPageIndex = Math.max(nextPageCount - 1, 0);

          setData(response.data.items);
          setPageCount(nextPageCount);
          setTotalElementsCount(response.data.totalCount);

          if (pageIndex > maximumPageIndex) {
            isHydratingPaginationFromURLRef.current = true;
            gotoPage(maximumPageIndex);
          }
        })
        .catch(error => {
          if (fetchRequestId === latestFetchRequestId.current) {
            console.error(error);
          }
        })
        .finally(() => {
          if (fetchRequestId === latestFetchRequestId.current) {
            hasCompletedInitialFetchRef.current = true;
            setIsRequestPending(false);
          }
        }),
    500
  );

  // Listen for changes in pagination and use the state to fetch our new data
  useEffect(() => {
    if (!fetchData) {
      return;
    }

    /**
     * We disable initial data fetch if default data is provided
     * Important None!!!: We do not add disableInitialLoad value to dependacies array.
     * This way, we do not retrigger the effect on setDisableInitialLoad - it would cause data reload.
     * Due to our current DataTable logic, that's the only way to achieve expected result.
     */
    if (disableInitialLoad) {
      setDisableInitialLoad(false);
      return;
    }

    /**
     * We are reaching for first element directly, because we re not supporting
     * multi sorting of columns. There will be likely only one column
     */
    if (!isDeleteRequestPending && !areTableDependeciesLoading) {
      const fetchRequestId = latestFetchRequestId.current + 1;
      latestFetchRequestId.current = fetchRequestId;

      cancelFetchData?.();
      setIsRequestPending(true);
      fetchAllData(fetchRequestId);
    }
    /**
     * disableInitialLoad is not added to dependecies purpously.
     * */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchData,
    cancelFetchData,
    pageIndex,
    pageSize,
    sortBy,
    reloadToken,
    isDeleteRequestPending,
    areTableDependeciesLoading,
  ]);

  useEffect(() => {
    if (fetchData || clientSidePagination) {
      return;
    }

    setData(defaultData);
  }, [defaultData, fetchData, clientSidePagination]);

  useEffect(() => {
    if (!shouldUseURLParams) {
      return;
    }
    if (paginationHydrationSearchRef.current === search) {
      return;
    }
    paginationHydrationSearchRef.current = search;

    const searchParams = new URLSearchParams(search);
    const pageIndexParam = searchParams.get(urlParamKeys.pageIndex);
    const requestedPageIndex = Number(pageIndexParam);
    const nextPageIndex =
      pageIndexParam !== null &&
      Number.isInteger(requestedPageIndex) &&
      requestedPageIndex >= 0
        ? requestedPageIndex
        : 0;
    const requestedPageSize = Number(
      searchParams.get(urlParamKeys.displayElementsNumber)
    );

    const pageSizeChanged =
      displayItemsNumberOptions.includes(requestedPageSize) &&
      requestedPageSize !== pageSize;
    if (pageSizeChanged) {
      setPageSize(requestedPageSize);
    }
    if (nextPageIndex !== pageIndex || pageSizeChanged) {
      // initialState already contains the requested URL page. gotoPage would
      // reject forward navigation against a previous scope's result count.
      // The fetched response still clamps pages that no longer exist.
      dispatch({ type: actions.resetPage });
    }
  }, [
    dispatch,
    displayItemsNumberOptions,
    pageIndex,
    pageSize,
    search,
    setPageSize,
    shouldUseURLParams,
    urlParamKeys,
  ]);

  const displayedTotalElementsCount = clientSidePagination
    ? tableData.length
    : totalElementsCount;
  useEffect(() => {
    onPaginationInfoChange?.({
      pageSize,
      pageIndex,
      totalElementsCount: displayedTotalElementsCount,
    });
  }, [
    displayedTotalElementsCount,
    onPaginationInfoChange,
    pageIndex,
    pageSize,
  ]);

  const TopBarComponent = topBar ?? DataTableTopBar;
  const shouldShowRequestLoader =
    isRequestPending &&
    (!preserveContentWhileRefetching || !hasCompletedInitialFetchRef.current);
  const isLoading = shouldShowRequestLoader || areTableDependeciesLoading;
  const hasData = tableData.length > 0 || Boolean(customLastBodyItem);

  const blankPage = customBlankPage || (
    <UIMessage
      className="no-results"
      image={NoResultsImage}
      title="No results found."
      message="Please check your search term for spelling and typos, or try removing filters."
    />
  );

  if (!isLoading && !hasData && emptyStateMode !== 'default') {
    return emptyStateMode === 'hidden' ? null : (
      <div className="tableWrapper blankPage">{blankPage}</div>
    );
  }

  return (
    <>
      {/* You can put filtering form in top bar */}
      {topBarConfig && (
        <TopBarComponent
          topbarConfig={topBarConfig}
          pageSize={pageSize}
          pageIndex={pageIndex}
          totalElementsCount={displayedTotalElementsCount}
          tableId={tableId}
          gotoPage={gotoPage}
        />
      )}
      {!isLoading && columns.length > 0 ? (
        <div
          className={classnames('tableWrapper', {
            'tableWrapper--grid': gridLayout,
            blankPage: !hasData,
          })}
        >
          <table
            className={classnames('dataTable', className, {
              'dataTable--grid': !!gridLayout,
            })}
            {...getTableProps()}
            cellPadding="10"
            data-testid={`${tableId}__table`}
          >
            {!noTableHeader && hasData ? (
              <DataTableHeader
                tableId={tableId}
                tableInstance={tableInstance}
              />
            ) : null}

            {hasData ? (
              <DataTableBody
                tableId={tableId}
                tableInstance={tableInstance}
                getRowEditPath={getRowEditPath}
                onRowRemove={onRowRemove}
                onRowDuplicate={onRowDuplicate}
                customBodyItem={customBodyItem}
                customLastBodyItem={customLastBodyItem}
                gridLayout={gridLayout}
                setData={setData}
              />
            ) : (
              blankPage
            )}
          </table>
        </div>
      ) : (
        <div
          className={classnames('tableWrapper', 'tableWrapper--loading', {
            'tableWrapper--grid': gridLayout,
          })}
        >
          <Loading visible testId="tableLoader" />
        </div>
      )}
      {!hidePagination && !isLoading && (
        <DataTablePagination
          tableId={tableId}
          tableInstance={tableInstance}
          displayItemsNumberOptions={displayItemsNumberOptions}
          itemName={itemName}
          hideDisplayedElementsDropdown={hideDisplayedElementsDropdown}
        />
      )}
    </>
  );
};

export default DataTable;
